/**
 * Aba Geral em modo edição (PR4 · gate `playbook-author`).
 *
 * Edita os campos REAIS do serializer: name · goal · successCriteria · vertical ·
 * ethicalBoundaries (redirectTopics + toneLimits). `status` e `version` seguem
 * leitura (status muda via arquivar/publicar — PR5; version é controle interno).
 * Campos do mockup sem fonte (description, idioma, tags) não entram — zero dado falso.
 *
 * **Persona SAIU dessa lista (Fase 0b, 05/Ago).** Ela estava excluída como «sem
 * fonte»; a decisão estava certa quando foi tomada e a premissa inverteu — o
 * serializer passou a expor `personaId` e `GET /v1/personas` existe. Enquanto
 * ninguém revisitou, nenhum tenant conseguia configurar a voz da própria IA
 * (a persona chega ao motor SÓ por `Playbook.personaId`). Lição registrada em
 * `docs/superpowers/specs/2026-08-05-arquitetura-de-conduta-e-contexto/`:
 * exclusão por «sem fonte» precisa de revisita quando a fonte nasce.
 */

import { VERTICAL_LABELS } from '../playbook-labels';
import { PlaybookStatusPill } from '../PlaybookStatusPill';

import { ChipListEditor } from './ChipListEditor';

import type { PlaybookDto, PlaybookVertical } from '../types';
import type { PlaybookForm } from '../use-playbook-form';

const VERTICAL_ORDER: PlaybookVertical[] = [
  'support',
  'commercial',
  'retention',
  'collections',
  'triage',
  'onboarding',
  'satisfaction_survey',
  'legal',
  'healthcare',
  'other',
];

/** O mínimo que o seletor de voz precisa saber de uma Persona. */
export interface PersonaOption {
  readonly id: string;
  readonly name: string;
}

export function TabGeneralEdit({
  form,
  playbook,
  personas,
}: {
  form: PlaybookForm;
  playbook: PlaybookDto;
  /**
   * Personas ativas do tenant, injetadas pelo container. O tab permanece PURO
   * de propósito — chamar `usePersonaList()` aqui exigiria QueryClient em todo
   * spec que renderiza a aba Geral.
   */
  personas: readonly PersonaOption[];
}): JSX.Element {
  const { draft, set } = form;

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Geral">
      <h2 className="pb-panel__title">Geral</h2>

      <div className="pb-field">
        <label className="pb-field__label" htmlFor="pb-name">
          Nome do playbook
        </label>
        <input
          id="pb-name"
          type="text"
          className="pb-input"
          value={draft.name}
          onChange={(e) => {
            set('name', e.target.value);
          }}
        />
      </div>

      <div className="pb-field">
        <label className="pb-field__label" htmlFor="pb-goal">
          Objetivo
        </label>
        <textarea
          id="pb-goal"
          className="pb-textarea"
          rows={2}
          value={draft.goal}
          onChange={(e) => {
            set('goal', e.target.value);
          }}
        />
      </div>

      <div className="pb-field">
        <label className="pb-field__label" htmlFor="pb-success">
          Critério de sucesso
        </label>
        <textarea
          id="pb-success"
          className="pb-textarea"
          rows={2}
          value={draft.successCriteria}
          onChange={(e) => {
            set('successCriteria', e.target.value);
          }}
        />
      </div>

      <div className="pb-grid-2">
        <div className="pb-field">
          <label className="pb-field__label" htmlFor="pb-vertical">
            Vertical
          </label>
          <select
            id="pb-vertical"
            className="pb-select"
            value={draft.vertical}
            onChange={(e) => {
              set('vertical', e.target.value as PlaybookVertical);
            }}
          >
            {VERTICAL_ORDER.map((v) => (
              <option key={v} value={v}>
                {VERTICAL_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
        <div className="pb-field">
          <span className="pb-field__label">Status</span>
          <div>
            <PlaybookStatusPill status={playbook.status} />
          </div>
        </div>
      </div>

      <div className="pb-field">
        <label className="pb-field__label" htmlFor="pb-persona">
          Persona (voz da IA)
        </label>
        <select
          id="pb-persona"
          className="pb-select"
          value={draft.personaId ?? ''}
          onChange={(e) => {
            set('personaId', e.target.value);
          }}
        >
          {/* `disabled`: o PATCH não expressa «nenhuma» (personaId é `string?` sem
              `| null` no DTO do Engine). A opção existe para RENDERIZAR o estado
              atual de um playbook sem persona, não para voltar a ele. */}
          <option value="" disabled>
            — nenhuma —
          </option>
          {personas.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {draft.personaId === null ? (
          <p className="pb-field__hint">
            Este playbook está sem voz configurada — a IA conduz sem persona. Ligue uma Persona para
            definir tom, vocabulário e limites de tom.
          </p>
        ) : null}
      </div>

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Limites éticos</span>
        <div className="pb-field">
          <span className="pb-field__label">Tópicos de redirecionamento</span>
          <ChipListEditor
            values={draft.ethicalBoundaries.redirectTopics}
            onChange={(next) => {
              set('ethicalBoundaries', { ...draft.ethicalBoundaries, redirectTopics: next });
            }}
            placeholder="ex.: cobranca"
            ariaLabel="Adicionar tópico de redirecionamento"
          />
        </div>
        <div className="pb-field">
          <span className="pb-field__label">Limites de tom</span>
          <ChipListEditor
            values={draft.ethicalBoundaries.toneLimits}
            onChange={(next) => {
              set('ethicalBoundaries', { ...draft.ethicalBoundaries, toneLimits: next });
            }}
            placeholder="ex.: nunca prometer prazo sem confirmar"
            ariaLabel="Adicionar limite de tom"
          />
        </div>
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-meta-row">
        <span>
          Versão atual: <strong>v{playbook.version}</strong>
        </span>
      </div>
    </div>
  );
}
